import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { VerificationBadge } from "../components/VerificationBadge.js";
import { stateLabel, type Participant } from "./Tournament.js";

/**
 * Arbiter panel for one tournament's entry list.
 *
 * The immediate job is removing spam registrations. Entries are **excluded**,
 * not deleted: an entry that has appeared in the published list should stay
 * traceable, and a genuine player removed by mistake can be reinstated.
 *
 * Contact details are fetched one player at a time rather than listed, so an
 * arbiter chasing one entrant does not pull 400 email addresses into a
 * browser tab. Every such access is logged server-side.
 */

interface Contact {
  nickname: string;
  email: string;
  phone: string | null;
  accountCreatedAt: string;
}

export function Admin() {
  const { slug } = useParams();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [tournament, setTournament] = useState<{ name: string; state: string } | null>(null);
  const [contacts, setContacts] = useState<Record<number, Contact>>({});
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    const res = await fetch(`/api/tournaments/${slug}/participants`);
    if (!res.ok) return;
    const data = await res.json();
    setParticipants(data.participants);
    setTournament(data.tournament);
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setState(p: Participant, state: Participant["state"]) {
    const reason =
      state === "excluded"
        ? window.prompt(`Why is ${p.nickname} being excluded?\n(recorded in the arbiter log)`)
        : null;
    // A null reason means the prompt was cancelled — do nothing.
    if (state === "excluded" && reason === null) return;

    setBusy(p.registrationId);
    const res = await fetch(`/api/admin/registrations/${p.registrationId}/state`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ state, reason: reason ?? undefined }),
    });
    if (res.status === 403) setForbidden(true);
    await load();
    setBusy(null);
  }

  async function showContact(p: Participant) {
    const res = await fetch(`/api/admin/registrations/${p.registrationId}/contact`);
    if (res.status === 403) {
      setForbidden(true);
      return;
    }
    if (res.ok) {
      const contact = (await res.json()) as Contact;
      setContacts((c) => ({ ...c, [p.registrationId]: contact }));
    }
  }

  if (forbidden) {
    return (
      <div style={{ maxWidth: 460 }}>
        <h2 style={{ marginTop: 0 }}>Arbiter access required</h2>
        <p style={{ color: "#555" }}>
          This page is for tournament arbiters. Your account does not have that role.
        </p>
      </div>
    );
  }

  const active = participants.filter((p) => p.state === "confirmed" || p.state === "pending");
  const removed = participants.filter((p) => p.state === "excluded" || p.state === "withdrawn");
  const unverified = active.filter((p) => p.verificationLevel === "none");

  return (
    <div>
      <h1 style={{ marginTop: 0, fontSize: 24 }}>{tournament?.name ?? "Tournament"}</h1>
      <p style={{ color: "#666", marginTop: -8, fontSize: 14 }}>
        Arbiter — entry list. Status: {tournament ? stateLabel(tournament.state) : "…"}
      </p>

      <div style={{ display: "flex", gap: 20, margin: "20px 0", flexWrap: "wrap" }}>
        <Stat label="Entered" value={active.length} />
        <Stat label="Not verified" value={unverified.length} tone={unverified.length ? "warn" : undefined} />
        <Stat label="Removed" value={removed.length} />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 14, width: "100%" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #e0e0e0" }}>
              <th style={th}>Player</th>
              <th style={th}>Name</th>
              <th style={th}>Country</th>
              <th style={th}>FMJD</th>
              <th style={th}>Verified</th>
              <th style={th}>Entered</th>
              <th style={th}>Contact</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => {
              const gone = p.state === "excluded" || p.state === "withdrawn";
              const contact = contacts[p.registrationId];
              return (
                <tr
                  key={p.registrationId}
                  style={{ borderBottom: "1px solid #f0f0f0", opacity: gone ? 0.5 : 1 }}
                >
                  <td style={{ ...td, fontWeight: 600 }}>
                    {p.nickname}
                    {gone && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#c62828" }}>
                        {p.state}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    {p.firstName} {p.lastName}
                  </td>
                  <td style={td}>{p.country ?? "—"}</td>
                  <td style={{ ...td, fontFamily: "ui-monospace, monospace" }}>
                    {p.fmjdId ?? "—"}
                  </td>
                  <td style={td}>
                    <VerificationBadge level={p.verificationLevel} />
                    {!p.emailVerified && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#e65100" }}>
                        no email
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: "#888", whiteSpace: "nowrap" }}>
                    {new Date(p.registeredAt).toLocaleDateString()}
                  </td>
                  <td style={{ ...td, fontSize: 13 }}>
                    {contact ? (
                      <span style={{ color: "#555" }}>
                        {contact.email}
                        {contact.phone && <br />}
                        {contact.phone}
                      </span>
                    ) : (
                      <button onClick={() => showContact(p)} style={linkButton}>
                        show
                      </button>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {gone ? (
                      <button
                        onClick={() => setState(p, "confirmed")}
                        disabled={busy === p.registrationId}
                        style={smallButton}
                      >
                        Reinstate
                      </button>
                    ) : (
                      <button
                        onClick={() => setState(p, "excluded")}
                        disabled={busy === p.registrationId}
                        style={{ ...smallButton, borderColor: "#ef9a9a", color: "#c62828" }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {participants.length === 0 && (
        <p style={{ color: "#888", fontSize: 14 }}>Nobody has entered yet.</p>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warn";
}) {
  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: "10px 18px",
        background: tone === "warn" ? "#fff8e1" : "#fff",
        minWidth: 110,
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: tone === "warn" ? "#e65100" : "#222" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "#777" }}>{label}</div>
    </div>
  );
}

const th: React.CSSProperties = { padding: "8px 12px 8px 0", fontWeight: 600, color: "#555" };
const td: React.CSSProperties = { padding: "8px 12px 8px 0", verticalAlign: "top" };

const smallButton: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 5,
  background: "#fff",
  cursor: "pointer",
};

const linkButton: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#00695c",
  fontSize: 13,
  cursor: "pointer",
  textDecoration: "underline",
};
